import { useState, useCallback, useRef } from 'react';

/**
 * Custom hook to prevent double-clicks and handle async actions safely
 *
 * @param {Function} action - The async function to execute
 * @param {Object} options - Optional configuration
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 * @returns {Object} - { execute, isLoading, error }
 *
 * @example
 * const { execute: handleSubmit, isLoading } = useAsyncAction(async () => {
 *   await axios.post('/api/endpoint', data);
 * });
 *
 * <button onClick={handleSubmit} disabled={isLoading}>
 *   {isLoading ? 'Saving...' : 'Save'}
 * </button>
 */
export function useAsyncAction(action, options = {}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const isExecutingRef = useRef(false);

  const execute = useCallback(async (...args) => {
    // Prevent double execution
    if (isExecutingRef.current || isLoading) {
      return;
    }

    isExecutingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const result = await action(...args);
      if (options.onSuccess) {
        options.onSuccess(result);
      }
      return result;
    } catch (err) {
      setError(err);
      if (options.onError) {
        options.onError(err);
      }
      throw err;
    } finally {
      setIsLoading(false);
      isExecutingRef.current = false;
    }
  }, [action, options.onSuccess, options.onError, isLoading]);

  return { execute, isLoading, error };
}

/**
 * Hook to manage multiple async actions with individual loading states
 * Useful for lists where each item can have its own action
 *
 * @returns {Object} - { executeWithId, isLoadingId, getButtonProps }
 *
 * @example
 * const { executeWithId, isLoadingId, getButtonProps } = useAsyncActionMap();
 *
 * const handleApprove = (id) => executeWithId(id, async () => {
 *   await axios.put(`/api/items/${id}/approve`);
 * });
 *
 * <button {...getButtonProps(item.id, () => handleApprove(item.id))}>
 *   {isLoadingId(item.id) ? 'Processing...' : 'Approve'}
 * </button>
 */
export function useAsyncActionMap() {
  const [loadingIds, setLoadingIds] = useState(new Set());
  const executingIdsRef = useRef(new Set());

  const isLoadingId = useCallback((id) => {
    return loadingIds.has(id);
  }, [loadingIds]);

  const executeWithId = useCallback(async (id, action) => {
    // Prevent double execution for the same ID
    if (executingIdsRef.current.has(id)) {
      return;
    }

    executingIdsRef.current.add(id);
    setLoadingIds(prev => new Set([...prev, id]));

    try {
      const result = await action();
      return result;
    } finally {
      executingIdsRef.current.delete(id);
      setLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const getButtonProps = useCallback((id, onClick) => {
    const loading = loadingIds.has(id);
    return {
      onClick: loading ? undefined : onClick,
      disabled: loading,
      className: loading ? 'opacity-50 cursor-not-allowed' : '',
    };
  }, [loadingIds]);

  return { executeWithId, isLoadingId, getButtonProps };
}

export default useAsyncAction;
